import { useCallback, useEffect, useMemo, useState } from "react";
import { Navigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import api from "../lib/api";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Checkbox } from "../components/ui/checkbox";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "../components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import { Trash2, Upload, Download, AlertTriangle, FileText, Search, FlaskConical, Sliders } from "lucide-react";
import { toast } from "sonner";
import { formatDate } from "../lib/utils";
import PlaceholderEditorDialog from "../components/documents/PlaceholderEditorDialog";

const CATEGORIES = [
  "Core policies", "HR & workforce", "Governance",
  "Service delivery", "Incident management", "Module-specific", "Registers",
];
const AUDIT_TYPES = ["Both", "Verification", "Certification"];
const MODULES = ["All", "Core", "M1", "M2", "M3", "M4", "M5"];

export default function TemplateLibrary() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [cat, setCat] = useState("all");
  const [uploadOpen, setUploadOpen] = useState(false);
  const [editorTpl, setEditorTpl] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/templates");
      setItems(r.data);
    } catch { toast.error("Failed to load templates"); }
    setLoading(false);
  }, []);
  useEffect(() => { if (user?.role === "admin") load(); }, [user, load]);

  const filtered = useMemo(() => items.filter((t) => {
    if (cat !== "all" && t.category !== cat) return false;
    if (q && !t.name.toLowerCase().includes(q.toLowerCase())) return false;
    return true;
  }), [items, q, cat]);

  const counts = useMemo(() => {
    const c = {};
    for (const t of items) c[t.category] = (c[t.category] || 0) + 1;
    return c;
  }, [items]);

  if (user && user.role !== "admin") return <Navigate to="/" replace />;

  async function onDelete(t) {
    if (!window.confirm(`Delete '${t.name}'? This cannot be undone.`)) return;
    try {
      await api.delete(`/templates/${t.id}`);
      toast.success("Template deleted");
      load();
    } catch { toast.error("Failed to delete"); }
  }
  async function onToggleActive(t) {
    try {
      await api.put(`/templates/${t.id}`, { active: !t.active });
      load();
    } catch { toast.error("Failed to update"); }
  }
  async function onDownload(t) {
    try {
      const r = await api.get(`/templates/${t.id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${t.name}.docx`;
      a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Download failed"); }
  }
  async function onTestRender(t) {
    try {
      const r = await api.post(`/templates/${t.id}/test-render`, null, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `TEST_${t.name}.docx`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Test render downloaded — sample client data injected");
    } catch { toast.error("Test render failed"); }
  }

  return (
    <div className="space-y-6" data-testid="template-library-page">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <p className="text-xs uppercase tracking-[0.2em] text-slate-500 mb-1">Admin · Template Library</p>
          <h1 className="text-3xl font-semibold tracking-tight text-[#0D1B2A]">Master policy templates</h1>
          <p className="text-sm text-slate-500 mt-1">
            {items.length} template{items.length === 1 ? "" : "s"} stored. Placeholders like <code className="text-xs bg-slate-100 px-1 rounded">&lt;company name&gt;</code> or <code className="text-xs bg-slate-100 px-1 rounded">{"{{client_name}}"}</code> get replaced with real client data at generation time.
          </p>
        </div>
        <Button onClick={() => setUploadOpen(true)} className="bg-[#1A7F6E] hover:bg-[#136053] text-white" data-testid="upload-template-btn">
          <Upload className="w-4 h-4 mr-2" />Upload template
        </Button>
      </div>

      <div className="flex flex-wrap gap-3 items-center">
        <div className="relative">
          <Search className="w-4 h-4 absolute left-2.5 top-2.5 text-slate-400" />
          <Input placeholder="Search templates..." value={q} onChange={(e) => setQ(e.target.value)} className="pl-8 w-64" data-testid="template-search" />
        </div>
        <Select value={cat} onValueChange={setCat}>
          <SelectTrigger className="w-56" data-testid="template-cat-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All categories ({items.length})</SelectItem>
            {CATEGORIES.map((c) => (
              <SelectItem key={c} value={c}>{c} ({counts[c] || 0})</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {loading ? (
        <p className="text-sm text-slate-500">Loading templates…</p>
      ) : (
        <div className="grid gap-3">
          {filtered.map((t) => (
            <Card key={t.id} className={`border-slate-200 ${t.active ? "" : "opacity-60"}`} data-testid={`template-row-${t.id}`}>
              <CardContent className="p-4 flex flex-col md:flex-row gap-4 md:items-center">
                <div className="flex-1 min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <FileText className="w-4 h-4 text-[#1A7F6E] shrink-0" />
                    <p className="font-medium text-[#0D1B2A] truncate">{t.name}</p>
                    {!t.active && <Badge variant="outline" className="text-xs">Inactive</Badge>}
                    {t.required_for_all && <Badge className="text-xs bg-[#E6F4F1] text-[#1A7F6E] hover:bg-[#E6F4F1]">Required for all</Badge>}
                  </div>
                  <div className="mt-1.5 flex flex-wrap items-center gap-2 text-xs text-slate-500">
                    <span>{t.category}</span>
                    <span>·</span>
                    <span>{(t.audit_types || []).join("/") || "Both"}</span>
                    <span>·</span>
                    <span>Modules: {(t.modules || []).join(", ") || "All"}</span>
                    <span>·</span>
                    <span>{(t.size_bytes / 1024).toFixed(0)} KB</span>
                    <span>·</span>
                    <span>v{t.version}</span>
                    <span>·</span>
                    <span>{formatDate(t.updated_at)}</span>
                  </div>
                  <PlaceholderChips placeholders={t.placeholders_known || []} unknown={t.placeholders_unknown || []} />
                </div>
                <div className="flex gap-2">
                  <Button size="sm" variant="outline" onClick={() => setEditorTpl(t)} title="Edit placeholder map" data-testid={`tpl-map-${t.id}`}>
                    <Sliders className="w-3.5 h-3.5" />
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => onTestRender(t)} title="Preview with dummy data" data-testid={`tpl-test-${t.id}`}>
                    <FlaskConical className="w-3.5 h-3.5" />
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => onDownload(t)} data-testid={`tpl-dl-${t.id}`}>
                    <Download className="w-3.5 h-3.5" />
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => onToggleActive(t)} data-testid={`tpl-toggle-${t.id}`}>
                    {t.active ? "Deactivate" : "Activate"}
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => onDelete(t)} className="text-rose-600 hover:text-rose-700" data-testid={`tpl-del-${t.id}`}>
                    <Trash2 className="w-3.5 h-3.5" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
          {filtered.length === 0 && (
            <p className="text-sm text-slate-500 py-8 text-center">No templates match.</p>
          )}
        </div>
      )}

      <UploadDialog open={uploadOpen} onOpenChange={setUploadOpen} onCreated={() => { setUploadOpen(false); load(); }} />

      <PlaceholderEditorDialog
        template={editorTpl}
        open={!!editorTpl}
        onOpenChange={(v) => !v && setEditorTpl(null)}
        onSaved={load}
      />
    </div>
  );
}

function PlaceholderChips({ placeholders, unknown }) {
  if (!placeholders.length && !unknown.length) return null;
  return (
    <div className="mt-2 flex flex-wrap gap-1">
      {placeholders.slice(0, 12).map((p) => (
        <span key={p.canonical} className="text-[10px] font-medium bg-[#E6F4F1] text-[#1A7F6E] px-1.5 py-0.5 rounded">
          {p.canonical} x{p.count}
        </span>
      ))}
      {placeholders.length > 12 && (
        <span className="text-[10px] text-slate-500">+{placeholders.length - 12} more</span>
      )}
      {(unknown || []).slice(0, 5).map((u) => (
        <span key={u} className="text-[10px] font-medium bg-rose-50 text-rose-700 px-1.5 py-0.5 rounded flex items-center gap-1" title="Unknown placeholder — will NOT be replaced">
          <AlertTriangle className="w-3 h-3" />&lt;{u}&gt;
        </span>
      ))}
    </div>
  );
}

function UploadDialog({ open, onOpenChange, onCreated }) {
  const [file, setFile] = useState(null);
  const [meta, setMeta] = useState({
    name: "", category: "Core policies", audit_types: "Both",
    modules: "All", required_for_all: "true", version: "1.0",
  });
  const [preview, setPreview] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!file) { setPreview(null); return; }
    if (!meta.name) setMeta((m) => ({ ...m, name: file.name.replace(/\.docx$/i, "") }));
  }, [file, meta.name]);

  async function submit() {
    if (!file) return toast.error("Pick a .docx file first");
    if (!meta.name) return toast.error("Give the template a name");
    setSaving(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      Object.entries(meta).forEach(([k, v]) => fd.append(k, v));
      const r = await api.post("/templates", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Template uploaded — ${r.data.placeholders_known.length} placeholder(s) detected`);
      setPreview(r.data);
      setFile(null);
      setMeta({ name: "", category: "Core policies", audit_types: "Both", modules: "All", required_for_all: "true", version: "1.0" });
      onCreated();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Upload failed");
    }
    setSaving(false);
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="upload-template-dialog">
        <DialogHeader><DialogTitle className="text-[#0D1B2A]">Upload master template</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div>
            <Label>Template file (.docx)</Label>
            <Input type="file" accept=".docx" onChange={(e) => setFile(e.target.files?.[0] || null)} data-testid="upload-file-input" />
          </div>
          <div>
            <Label>Name</Label>
            <Input value={meta.name} onChange={(e) => setMeta((m) => ({ ...m, name: e.target.value }))} data-testid="upload-name-input" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Category</Label>
              <Select value={meta.category} onValueChange={(v) => setMeta((m) => ({ ...m, category: v }))}>
                <SelectTrigger data-testid="upload-cat-select"><SelectValue /></SelectTrigger>
                <SelectContent>{CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Audit types</Label>
              <Select value={meta.audit_types} onValueChange={(v) => setMeta((m) => ({ ...m, audit_types: v }))}>
                <SelectTrigger data-testid="upload-audit-select"><SelectValue /></SelectTrigger>
                <SelectContent>{AUDIT_TYPES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Modules</Label>
              <Select value={meta.modules} onValueChange={(v) => setMeta((m) => ({ ...m, modules: v }))}>
                <SelectTrigger data-testid="upload-modules-select"><SelectValue /></SelectTrigger>
                <SelectContent>{MODULES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Version</Label>
              <Input value={meta.version} onChange={(e) => setMeta((m) => ({ ...m, version: e.target.value }))} />
            </div>
          </div>
          <label className="flex items-center gap-2 text-sm">
            <Checkbox checked={meta.required_for_all === "true"} onCheckedChange={(v) => setMeta((m) => ({ ...m, required_for_all: v ? "true" : "false" }))} data-testid="upload-required-check" />
            <span>Required for all clients</span>
          </label>
          {preview && preview.placeholders_known && (
            <div className="border border-slate-200 rounded p-3">
              <p className="text-xs uppercase tracking-widest text-slate-500 mb-1">Detected placeholders</p>
              <PlaceholderChips placeholders={preview.placeholders_known} unknown={preview.placeholders_unknown} />
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={submit} disabled={saving} className="bg-[#1A7F6E] hover:bg-[#136053] text-white" data-testid="upload-submit-btn">
            {saving ? "Uploading…" : "Upload"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
